import React, { useState, useEffect, useRef, useMemo } from 'react';
import TicketInventoryService from '../../services/TicketInventoryService';
import FacilityService from '../../services/FacilityService';
import UserService from '../../services/UserService';
import util from '../../utils/util';
import { If } from '../../utils/Controls';
import { AntdPaging } from '../../utils/Antd';
import { saveAs } from 'file-saver';
import logs from '../../utils/logs';

// ─── Event type display config ───────────────────────────────────────────────
const EVENT_META = {
    page_view:            { label: 'Page View',        icon: 'fa-eye',               color: '#8c8c8c', bg: '#f5f5f5' },
    tab_change:           { label: 'Tab Change',        icon: 'fa-exchange',          color: '#595959', bg: '#f5f5f5' },
    link_click:           { label: 'Link Click',        icon: 'fa-mouse-pointer',     color: '#595959', bg: '#f5f5f5' },
    login:                { label: 'Login',             icon: 'fa-sign-in',           color: '#52c41a', bg: '#f6ffed' },
    logout:               { label: 'Logout',            icon: 'fa-sign-out',          color: '#fa8c16', bg: '#fff7e6' },
    record_create:        { label: 'Created',           icon: 'fa-plus-circle',       color: '#52c41a', bg: '#f6ffed' },
    record_update:        { label: 'Updated',           icon: 'fa-pencil',            color: '#1890ff', bg: '#e6f7ff' },
    record_delete:        { label: 'Deleted',           icon: 'fa-trash',             color: '#ff4d4f', bg: '#fff2f0' },
    record_view:          { label: 'Viewed',            icon: 'fa-eye',               color: '#8c8c8c', bg: '#f5f5f5' },
    form_submit:          { label: 'Form Submit',       icon: 'fa-check-square-o',    color: '#1890ff', bg: '#e6f7ff' },
    form_field_change:    { label: 'Field Changed',     icon: 'fa-keyboard-o',        color: '#8c8c8c', bg: '#f5f5f5' },
    form_validation_fail: { label: 'Validation Failed', icon: 'fa-warning',           color: '#fa8c16', bg: '#fff7e6' },
    file_upload:          { label: 'File Uploaded',     icon: 'fa-upload',            color: '#722ed1', bg: '#f9f0ff' },
    file_download:        { label: 'File Downloaded',   icon: 'fa-download',          color: '#13c2c2', bg: '#e6fffb' },
    file_delete:          { label: 'File Deleted',      icon: 'fa-times-circle',      color: '#ff4d4f', bg: '#fff2f0' },
    document_view:        { label: 'Document Viewed',   icon: 'fa-file-text-o',       color: '#8c8c8c', bg: '#f5f5f5' },
    notice_create:        { label: 'Notice Posted',     icon: 'fa-bullhorn',          color: '#722ed1', bg: '#f9f0ff' },
    notice_update:        { label: 'Notice Updated',    icon: 'fa-bullhorn',          color: '#1890ff', bg: '#e6f7ff' },
    notice_delete:        { label: 'Notice Deleted',    icon: 'fa-bullhorn',          color: '#ff4d4f', bg: '#fff2f0' },
    notice_read:          { label: 'Notice Read',       icon: 'fa-envelope-open',     color: '#8c8c8c', bg: '#f5f5f5' },
    report_generate:      { label: 'Report Generated',  icon: 'fa-bar-chart',         color: '#13c2c2', bg: '#e6fffb' },
    report_export:        { label: 'Report Exported',   icon: 'fa-file-excel-o',      color: '#52c41a', bg: '#f6ffed' },
    search:               { label: 'Searched',          icon: 'fa-search',            color: '#8c8c8c', bg: '#f5f5f5' },
    filter_apply:         { label: 'Filter Applied',    icon: 'fa-filter',            color: '#8c8c8c', bg: '#f5f5f5' },
    error:                { label: 'Error',             icon: 'fa-exclamation-circle', color: '#ff4d4f', bg: '#fff2f0' },
    api_fail:             { label: 'API Failure',       icon: 'fa-wifi',              color: '#ff4d4f', bg: '#fff2f0' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDT = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-SZ', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
};

const EventBadge = ({ eventType }) => {
    const m = EVENT_META[eventType] || { label: eventType, icon: 'fa-circle', color: '#8c8c8c', bg: '#f5f5f5' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '2px 9px', borderRadius: '12px', fontSize: '11px',
            fontWeight: '600', color: m.color, background: m.bg,
            border: `1px solid ${m.color}33`, whiteSpace: 'nowrap'
        }}>
            <i className={`fa ${m.icon}`}></i> {m.label}
        </span>
    );
};

const StatCard = ({ icon, label, value, color }) => (
    <div style={{
        background: '#fff', borderRadius: '8px', border: '1px solid #e8e8e8',
        padding: '16px', display: 'flex', alignItems: 'center', gap: '14px'
    }}>
        <div style={{
            width: '44px', height: '44px', borderRadius: '10px',
            background: `${color}18`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0
        }}>
            <i className={`fa ${icon}`} style={{ fontSize: '20px', color }}></i>
        </div>
        <div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#262626', lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '3px' }}>{label}</div>
        </div>
    </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Logs = () => {
    const isNational = util.isNational();
    const isAdmin    = util.isAdmin ? util.isAdmin() : isNational;

    const [result,  setResult]  = useState({ data: [], page: {} });
    const [result2, setResult2] = useState({ data: [], page: {} });
    const [clients, setClients] = useState([]);
    const [users,   setUsers]   = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedLog, setExpandedLog] = useState(null);

    const sdataRef = useRef({ p: 1, ps: 50 });

    const [filters, setFilters] = useState({
        user:      '',
        eventType: '',
        module:    '',
        from_date: '',
        to_date:   '',
        k:         '',
        p:         1,
        ps:        50,
    });

    // ── Load reference data once ─────────────────────────────
    useEffect(() => {
        FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
            setClients(res.data.result.data.map(v => ({
                id: v.id, name: v.business_name, is_me: v.is_me
            })));
        });

        if (isAdmin) {
            UserService.allUsers({ status: 1 }).then(r => {
                if (r.data.code === 200) setUsers(r.data.result?.data || []);
            }).catch(() => {});
        }

        return () => {};
    }, []);

    // ── Fetch logs whenever filters change ───────────────────
    useEffect(() => {
        loadLogs();
    }, [filters]);

    const loadLogs = () => {
        sdataRef.current = { ...sdataRef.current, ...filters };
        setLoading(true);

        // Paginated detail log
        TicketInventoryService.lists1131(sdataRef.current).then(({ data }) => {
            setResult({ ...data.result });
        }).catch(e => {
            console.error(e.message);
        }).finally(() => setLoading(false));

        // Summary totals by event type
        TicketInventoryService.lists113(sdataRef.current).then(({ data }) => {
            setResult2({ ...data.result });
        }).catch(e => console.error(e.message));
    };

    // ── Analytics derived from current page data ─────────────
    const analytics = useMemo(() => {
        const byEvent  = {};
        const byModule = {};
        const byUser   = {};
        const byHour   = Array(24).fill(0);
        let criticalActions = 0;
        const criticalTypes = ['record_delete', 'file_delete', 'notice_delete', 'error', 'api_fail'];

        result.data.forEach(log => {
            byEvent[log.eventType]  = (byEvent[log.eventType]  || 0) + 1;
            if (log.module) byModule[log.module] = (byModule[log.module] || 0) + 1;
            if (log.user)   byUser[log.user]     = (byUser[log.user]     || 0) + 1;
            if (log.timestamp) {
                const h = new Date(log.timestamp).getHours();
                if (!isNaN(h)) byHour[h]++;
            }
            if (criticalTypes.includes(log.eventType)) criticalActions++;
        });

        const topModules = Object.entries(byModule).sort((a, b) => b[1] - a[1]).slice(0, 5);
        const topUsers   = Object.entries(byUser).sort((a, b) => b[1] - a[1]).slice(0, 5);
        const topEvents  = Object.entries(byEvent).sort((a, b) => b[1] - a[1]).slice(0, 6);
        const peakHour   = byHour.indexOf(Math.max(...byHour));

        return { byEvent, byModule, byUser, byHour, topModules, topUsers, topEvents, criticalActions, peakHour };
    }, [result.data]);

    // ── Export ────────────────────────────────────────────────
    const exportCSV = () => {
        sdataRef.current.p  = 1;
        sdataRef.current.ps = 0;
        setLoading(true);
        TicketInventoryService.lists1131(sdataRef.current).then(({ data }) => {
            const header = {
                user: 'USERNAME', eventType: 'Event Type', timestamp: 'Time Stamp',
                ipAddress: 'IP Address', deviceType: 'Device Type', module: 'Module', details: 'Details',
            };
            const blob = util.convertToCSVBlob(data.result.data, header);
            saveAs(blob, `logs-${new Date().toISOString().split('T')[0]}.csv`);
        }).catch(e => console.error(e.message))
          .finally(() => setLoading(false));
    };

    const exportPDF = () => {
        logs.generateStockTransactionsReport(sdataRef, util.Orientation.LANSCAPE);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, p: 1 }));
    };

    const clearFilters = () => setFilters({
        user: '', eventType: '', module: '', from_date: '', to_date: '', k: '', p: 1, ps: 50
    });

    const totalPages = result.page.total_pages || Math.ceil((result.page.total_records || 0) / filters.ps);

    const parseDetails = (details) => {
        if (!details) return null;
        if (typeof details === 'object') return details;
        try { return JSON.parse(details); } catch { return { raw: details }; }
    };

    return (
        <div className="page-content">
            {/* ── Header ─────────────────────────────────────── */}
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Logs Report &amp; Summary</h2>
                    <p style={{ margin: 0, color: '#8c8c8c', fontSize: '14px' }}>
                        Complete audit trail of all user actions across the system
                    </p>
                </div>
                <div className="ml-auto my-auto d-flex" style={{ gap: '8px' }}>
                    <button className="btn btn-default" onClick={exportCSV} disabled={result.data.length === 0}>
                        <i className="fa fa-file-excel-o"></i> Export CSV
                    </button>
                    <button className="btn btn-danger" onClick={exportPDF} disabled={result.data.length === 0}>
                        <i className="fa fa-file-pdf-o"></i> Export PDF
                    </button>
                    <button className="btn btn-primary" onClick={loadLogs}>
                        <i className="fa fa-refresh"></i> Refresh
                    </button>
                </div>
            </div>

            <div className="page-pad">

                {/* ── Event type summary tiles ────────────────── */}
                {result2.data.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                        {result2.data.map((i, v) => {
                            const m = EVENT_META[i.eventType] || { label: i.eventType, icon: 'fa-circle', color: '#1890ff', bg: '#e6f7ff' };
                            return (
                                <div key={v} style={{
                                    background: m.bg, border: `1px solid ${m.color}33`,
                                    borderRadius: '8px', padding: '10px 16px',
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                    outline: filters.eventType === i.eventType ? `2px solid ${m.color}` : 'none'
                                }}
                                onClick={() => handleFilterChange('eventType', filters.eventType === i.eventType ? '' : i.eventType)}
                                >
                                    <i className={`fa ${m.icon}`} style={{ color: m.color, fontSize: '18px' }}></i>
                                    <div>
                                        <div style={{ fontSize: '11px', color: m.color, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{m.label}</div>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#262626', lineHeight: 1 }}>{i.eventTotal}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ── Filters ─────────────────────────────────── */}
                <div className="card" style={{ marginBottom: '16px' }}>
                    <div className="card-header" style={{ padding: '12px 16px' }}>
                        <strong><i className="fa fa-filter" style={{ marginRight: '6px' }}></i> Filters</strong>
                    </div>
                    <div className="card-body" style={{ padding: '14px 16px' }}>
                        <div className="row">
                            {isAdmin && (
                                <div className="col-md-3">
                                    <label style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px', display: 'block' }}>User</label>
                                    <select className="form-control form-control-sm" value={filters.user}
                                        onChange={e => handleFilterChange('user', e.target.value)}>
                                        <option value="">All Users</option>
                                        {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                                    </select>
                                </div>
                            )}
                            <div className="col-md-3">
                                <label style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px', display: 'block' }}>Event Type</label>
                                <select className="form-control form-control-sm" value={filters.eventType}
                                    onChange={e => handleFilterChange('eventType', e.target.value)}>
                                    <option value="">All Events</option>
                                    {Object.entries(EVENT_META).map(([k, v]) => (
                                        <option key={k} value={k}>{v.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px', display: 'block' }}>Module</label>
                                <input type="text" className="form-control form-control-sm" placeholder="e.g. Assets"
                                    value={filters.module}
                                    onChange={e => handleFilterChange('module', e.target.value)} />
                            </div>
                            <div className="col-md-2">
                                <label style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px', display: 'block' }}>From</label>
                                <input type="date" className="form-control form-control-sm"
                                    value={filters.from_date}
                                    onChange={e => handleFilterChange('from_date', e.target.value)} />
                            </div>
                            <div className="col-md-2">
                                <label style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px', display: 'block' }}>To</label>
                                <input type="date" className="form-control form-control-sm"
                                    value={filters.to_date}
                                    onChange={e => handleFilterChange('to_date', e.target.value)} />
                            </div>
                        </div>
                        <div className="row" style={{ marginTop: '10px' }}>
                            <div className="col-md-8">
                                <div style={{ position: 'relative' }}>
                                    <input type="text" className="form-control form-control-sm"
                                        placeholder="Search by action, module, details..."
                                        value={filters.k}
                                        onChange={e => handleFilterChange('k', e.target.value)}
                                        style={{ paddingLeft: '32px' }} />
                                    <i className="fa fa-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#8c8c8c', fontSize: '13px' }}></i>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <button className="btn btn-default btn-sm" style={{ width: '100%' }} onClick={clearFilters}>
                                    <i className="fa fa-times"></i> Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Stat cards ──────────────────────────────── */}
                {result.data.length > 0 && (
                    <div className="row" style={{ marginBottom: '16px' }}>
                        <div className="col-md-3">
                            <StatCard icon="fa-list"                 label="Records (this page)" value={result.data.length.toLocaleString()} color="#1890ff" />
                        </div>
                        <div className="col-md-3">
                            <StatCard icon="fa-users"                label="Unique Users"         value={Object.keys(analytics.byUser).length}   color="#52c41a" />
                        </div>
                        <div className="col-md-3">
                            <StatCard icon="fa-sitemap"              label="Modules Touched"      value={Object.keys(analytics.byModule).length} color="#722ed1" />
                        </div>
                        <div className="col-md-3">
                            <StatCard icon="fa-exclamation-triangle" label="Critical Actions"     value={analytics.criticalActions}              color="#ff4d4f" />
                        </div>
                    </div>
                )}

                {/* ── Breakdown cards ─────────────────────────── */}
                {result.data.length > 0 && (
                    <div className="row" style={{ marginBottom: '16px' }}>
                        {/* Top modules */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header" style={{ padding: '10px 14px' }}>
                                    <strong style={{ fontSize: '13px' }}>
                                        <i className="fa fa-sitemap" style={{ marginRight: '6px', color: '#722ed1' }}></i> Top Modules
                                    </strong>
                                </div>
                                <div className="card-body" style={{ padding: '10px 14px' }}>
                                    {analytics.topModules.length === 0
                                        ? <p style={{ color: '#8c8c8c', fontSize: '13px' }}>No data</p>
                                        : analytics.topModules.map(([mod, cnt]) => (
                                            <div key={mod} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <span style={{ fontSize: '13px', color: '#262626' }}>{mod}</span>
                                                <span style={{ fontWeight: '700', fontSize: '13px', color: '#1890ff', background: '#e6f7ff', padding: '1px 8px', borderRadius: '10px' }}>{cnt}</span>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Event breakdown */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header" style={{ padding: '10px 14px' }}>
                                    <strong style={{ fontSize: '13px' }}>
                                        <i className="fa fa-bolt" style={{ marginRight: '6px', color: '#fa8c16' }}></i> Event Breakdown
                                    </strong>
                                </div>
                                <div className="card-body" style={{ padding: '10px 14px' }}>
                                    {analytics.topEvents.length === 0
                                        ? <p style={{ color: '#8c8c8c', fontSize: '13px' }}>No data</p>
                                        : analytics.topEvents.map(([evt, cnt]) => {
                                            const m = EVENT_META[evt] || { label: evt, color: '#8c8c8c', icon: 'fa-circle', bg: '#f5f5f5' };
                                            return (
                                                <div key={evt} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                                        <i className={`fa ${m.icon}`} style={{ color: m.color, width: '14px' }}></i>
                                                        {m.label}
                                                    </span>
                                                    <span style={{ fontWeight: '700', fontSize: '13px', color: m.color, background: m.bg, padding: '1px 8px', borderRadius: '10px' }}>{cnt}</span>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Top users */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header" style={{ padding: '10px 14px' }}>
                                    <strong style={{ fontSize: '13px' }}>
                                        <i className="fa fa-users" style={{ marginRight: '6px', color: '#52c41a' }}></i> Most Active Users
                                    </strong>
                                </div>
                                <div className="card-body" style={{ padding: '10px 14px' }}>
                                    {analytics.topUsers.length === 0
                                        ? <p style={{ color: '#8c8c8c', fontSize: '13px' }}>No data</p>
                                        : analytics.topUsers.map(([user, cnt], idx) => (
                                            <div key={user} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div style={{
                                                        width: '22px', height: '22px', borderRadius: '50%',
                                                        background: ['#1890ff','#52c41a','#fa8c16','#722ed1','#eb2f96'][idx] || '#8c8c8c',
                                                        color: '#fff', display: 'flex', alignItems: 'center',
                                                        justifyContent: 'center', fontSize: '10px', fontWeight: '700', flexShrink: 0
                                                    }}>{idx + 1}</div>
                                                    <span style={{ fontSize: '13px', color: '#262626' }}>{user}</span>
                                                </div>
                                                <span style={{ fontWeight: '700', fontSize: '13px', color: '#52c41a', background: '#f6ffed', padding: '1px 8px', borderRadius: '10px' }}>{cnt}</span>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Log table ───────────────────────────────── */}
                <div className="card">
                    <div className="card-header" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong><i className="fa fa-history" style={{ marginRight: '6px' }}></i> Activity Log</strong>
                        <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                            {loading ? 'Loading...' : `${(result.page.total_records || 0).toLocaleString()} total records`}
                        </span>
                    </div>

                    <div className="card-body" style={{ padding: 0 }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '60px', color: '#8c8c8c' }}>
                                <i className="fa fa-spinner fa-spin fa-2x"></i>
                                <p style={{ marginTop: '10px' }}>Loading activity trail...</p>
                            </div>
                        ) : result.data.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px' }}>
                                <i className="fa fa-history" style={{ fontSize: '48px', color: '#d9d9d9' }}></i>
                                <p style={{ marginTop: '12px', color: '#8c8c8c' }}>No activity records found for the selected filters</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table" style={{ margin: 0, fontSize: '13px' }}>
                                    <thead style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
                                        <tr>
                                            <th style={{ padding: '10px 14px', border: 'none', fontWeight: '600', color: '#595959', width: '155px' }}>Timestamp</th>
                                            <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '130px' }}>User</th>
                                            <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '145px' }}>Event</th>
                                            <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '110px' }}>Module</th>
                                            <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959' }}>Action / Details</th>
                                            <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '90px'  }}>Device</th>
                                            <th style={{ padding: '10px 8px',  border: 'none', fontWeight: '600', color: '#595959', width: '115px' }}>IP Address</th>
                                            <th style={{ padding: '10px 14px', border: 'none', width: '36px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.data.map((log, idx) => {
                                            const isCritical = ['record_delete','file_delete','error','api_fail'].includes(log.eventType);
                                            const isExpanded = expandedLog === (log.id || idx);
                                            const details    = parseDetails(log.details);
                                            return (
                                                <React.Fragment key={log.id || idx}>
                                                    <tr style={{
                                                        background:  isCritical ? '#fff2f0' : (idx % 2 === 0 ? '#fff' : '#fafafa'),
                                                        borderBottom: '1px solid #f0f0f0',
                                                        borderLeft:   isCritical ? '3px solid #ff4d4f' : '3px solid transparent'
                                                    }}>
                                                        <td style={{ padding: '10px 14px', verticalAlign: 'middle', color: '#595959', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                            {formatDT(log.timestamp)}
                                                        </td>
                                                        <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <div style={{
                                                                    width: '26px', height: '26px', borderRadius: '50%',
                                                                    background: '#1890ff', color: '#fff', display: 'flex',
                                                                    alignItems: 'center', justifyContent: 'center',
                                                                    fontSize: '11px', fontWeight: '700', flexShrink: 0
                                                                }}>
                                                                    {(log.user || '?')[0].toUpperCase()}
                                                                </div>
                                                                <span style={{ fontWeight: '500', color: '#262626' }}>{log.user || '—'}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                                            <EventBadge eventType={log.eventType} />
                                                        </td>
                                                        <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                                            <span style={{ fontSize: '12px', background: '#f0f0f0', padding: '2px 7px', borderRadius: '10px', color: '#595959' }}>
                                                                {log.module || '—'}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                                                            <div style={{ fontWeight: '500', color: '#262626' }}>{log.action || log.details || '—'}</div>
                                                            {log.url && (
                                                                <div style={{ fontSize: '11px', color: '#bfbfbf', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px' }}>
                                                                    {log.url}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '12px', color: '#8c8c8c' }}>
                                                            <i className={`fa ${log.deviceType === 'Mobile' ? 'fa-mobile' : log.deviceType === 'Tablet' ? 'fa-tablet' : 'fa-desktop'}`} style={{ marginRight: '4px' }}></i>
                                                            {log.deviceType || '—'}
                                                        </td>
                                                        <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '12px', color: '#8c8c8c', fontFamily: 'monospace' }}>
                                                            {log.ipAddress || '—'}
                                                        </td>
                                                        <td style={{ padding: '10px 14px', verticalAlign: 'middle' }}>
                                                            {details && Object.keys(details).length > 0 && (
                                                                <button className="btn btn-default btn-xs"
                                                                    onClick={() => setExpandedLog(isExpanded ? null : (log.id || idx))}
                                                                    title="View details">
                                                                    <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>

                                                    {/* Expanded detail row */}
                                                    {isExpanded && details && (
                                                        <tr style={{ background: '#f8faff' }}>
                                                            <td colSpan={8} style={{ padding: '10px 24px 14px' }}>
                                                                <div style={{ fontSize: '12px', color: '#595959', fontWeight: '600', marginBottom: '6px' }}>
                                                                    <i className="fa fa-info-circle" style={{ marginRight: '4px', color: '#1890ff' }}></i> Event Details
                                                                </div>
                                                                <div style={{
                                                                    background: '#fff', border: '1px solid #e8e8e8', borderRadius: '6px',
                                                                    padding: '10px 14px', display: 'grid',
                                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px'
                                                                }}>
                                                                    {Object.entries(details).map(([k, v]) => (
                                                                        <div key={k}>
                                                                            <div style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                                {k.replace(/_/g, ' ')}
                                                                            </div>
                                                                            <div style={{ fontSize: '13px', color: '#262626', wordBreak: 'break-all' }}>
                                                                                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {!loading && result.data.length > 0 && (
                            <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                                    {result.page.start != null
                                        ? `${result.page.start + 1}–${result.page.start + result.page.total} of ${(result.page.total_records || 0).toLocaleString()}`
                                        : `${(result.page.total_records || 0).toLocaleString()} records`
                                    }
                                </span>
                                <AntdPaging
                                    onChange={(p, ps) => setFilters(prev => ({ ...prev, p, ps }))}
                                    total={result.page.total_records || 0}
                                    current={result.page.cur_page   || filters.p}
                                    pageSize={filters.ps}
                                    showSizeChanger
                                />
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Logs;